import { visualEditorApi, skillsApi } from '@/services/api/agentApi';
import { useDebugStore } from '@/stores/ai/debug-store';

interface TrackedGeneration {
  threadId: string;
  componentIds: string[];
  committedAt: number;
  feedbackSent: boolean;
  /** When set, skill feedback is sent alongside visual-editor feedback. */
  skillId?: string;
}

const tracked = new Map<string, TrackedGeneration>();
const timers = new Map<string, NodeJS.Timeout>();

const TAG = '[PostActionTracker]';

/** Fire-and-forget skill feedback if a skill is associated with this generation. */
function sendSkillFeedback(gen: TrackedGeneration, success: boolean) {
  if (gen.skillId) {
    skillsApi.feedback(gen.skillId, success).catch((err) => {
      console.warn(TAG, 'Failed to send skill feedback', { skillId: gen.skillId, success, error: (err as Error).message });
    });
  }
}

/**
 * PostActionTracker — implicit feedback loop for agent-generated content.
 *
 * After a streaming batch commits, we start tracking. If the user does NOT undo
 * within 30 seconds, we send positive feedback. If they undo (or the streaming
 * batch is rolled back), we send negative feedback immediately.
 */
export const PostActionTracker = {
  startTracking(threadId: string, componentIds: string[]) {
    if (tracked.has(threadId)) return;
    console.log(TAG, 'Tracking started', { threadId, componentCount: componentIds.length, componentIds });
    tracked.set(threadId, { threadId, componentIds, committedAt: Date.now(), feedbackSent: false });
    const timer = setTimeout(() => {
      const gen = tracked.get(threadId);
      if (gen && !gen.feedbackSent) {
        gen.feedbackSent = true;
        console.log(TAG, 'Sending POSITIVE feedback (30s timer expired, no undo)', { threadId });
        visualEditorApi.feedback(threadId, 'positive').catch((err) => {
          console.warn(TAG, 'Failed to send positive feedback', { threadId, error: (err as Error).message });
        });
        sendSkillFeedback(gen, true);
        if (useDebugStore.getState().isEnabled) useDebugStore.getState().setFeedbackSignal('positive');
      }
      timers.delete(threadId);
      tracked.delete(threadId);
    }, 30000);
    timers.set(threadId, timer);
  },

  /** Associate a skill ID with an already-tracked generation for feedback. */
  associateSkill(threadId: string, skillId: string) {
    const gen = tracked.get(threadId);
    if (gen) {
      gen.skillId = skillId;
      console.log(TAG, 'Skill associated', { threadId, skillId });
    }
  },

  handleUndo(undoneComponentIds: string[]) {
    for (const [threadId, gen] of tracked.entries()) {
      if (gen.feedbackSent) continue;
      const overlap = gen.componentIds.some(id => undoneComponentIds.includes(id));
      if (overlap) {
        gen.feedbackSent = true;
        const timer = timers.get(threadId);
        if (timer) { clearTimeout(timer); timers.delete(threadId); }
        tracked.delete(threadId);
        console.log(TAG, 'Sending NEGATIVE feedback (undo detected)', { threadId, undoneComponentIds });
        visualEditorApi.feedback(threadId, 'negative').catch((err) => {
          console.warn(TAG, 'Failed to send negative feedback', { threadId, error: (err as Error).message });
        });
        sendSkillFeedback(gen, false);
        if (useDebugStore.getState().isEnabled) useDebugStore.getState().setFeedbackSignal('negative');
      }
    }
  },

  sendImmediateNegative(threadId: string) {
    const gen = tracked.get(threadId);
    if (gen && !gen.feedbackSent) {
      gen.feedbackSent = true;
      const timer = timers.get(threadId);
      if (timer) { clearTimeout(timer); timers.delete(threadId); }
      tracked.delete(threadId);
      console.log(TAG, 'Sending NEGATIVE feedback (immediate rollback)', { threadId });
      visualEditorApi.feedback(threadId, 'negative').catch((err) => {
        console.warn(TAG, 'Failed to send negative feedback', { threadId, error: (err as Error).message });
      });
      sendSkillFeedback(gen, false);
      if (useDebugStore.getState().isEnabled) useDebugStore.getState().setFeedbackSignal('negative');
    }
  },

  sendCorrection(threadId: string, correctionText: string, componentIds?: string[]) {
    console.log(TAG, 'Sending CORRECTION feedback', { threadId, correctionText: correctionText.slice(0, 100), componentIds });
    visualEditorApi.feedback(threadId, 'negative', correctionText, componentIds).catch((err) => {
      console.warn(TAG, 'Failed to send correction feedback', { threadId, error: (err as Error).message });
    });
  },

  cleanup() {
    console.log(TAG, 'Cleanup', { trackedCount: tracked.size, timerCount: timers.size });
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    tracked.clear();
  },
};
